import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as adminApi from '../api/adminApi';
import { PAGE_SIZE, formatMoney, formatPlacedDate, formatPlacedTime, HISTORY_ORDER_STATUSES } from '../lib/format';
import { normalizeListResponse, pickErrorMessage } from '../lib/apiHelpers';
import {
  humanOrderStatus,
  formatOrderTitleSummary,
  lineItemCount,
  orderDisplayNumber,
  orderRowId,
  pickAppUserId,
  pickCustomerEmail,
  pickCustomerName,
  pickCustomerPhone,
  pickDeliveryContactPhone,
  pickDeliveryRecipientName,
  pickPaymentSummary,
  pickTotalAmount,
  isPickupOrder,
} from '../lib/orders';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';
import { FilterSubmitButton } from '../components/FilterSubmitButton';
import { CommissionFlowLink } from '../components/CommissionFlowLink';

const EMPTY_FILTERS = {
  status: '',
  orderType: '',
  orderNumberContains: '',
  customerNameContains: '',
  customerMobileContains: '',
  itemTitleContains: '',
  dateFrom: '',
  dateTo: '',
};

function startOfDayIso(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const t = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return `${t}T00:00:00.000Z`;
  return t;
}

function endOfDayIso(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const t = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return `${t}T23:59:59.999Z`;
  return t;
}

function statusBadge(status) {
  const map = {
    CONFIRMED: 'info',
    PREPARING: 'warning',
    READY_FOR_PICKUP: 'primary',
    ON_THE_WAY: 'primary',
    DELIVERED: 'success',
    REFUNDED: 'warning',
    CANCELLED: 'danger',
  };
  const c = map[status] || 'secondary';
  return <span className={`badge text-bg-${c}`}>{humanOrderStatus(status)}</span>;
}

export default function OrderHistory() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { skip, limit };
      if (applied.status) params.status = applied.status;
      if (applied.orderType) params.orderType = applied.orderType;
      if (applied.orderNumberContains.trim()) {
        params.orderNumberContains = applied.orderNumberContains.trim();
      }
      if (applied.customerNameContains.trim()) {
        params.customerNameContains = applied.customerNameContains.trim();
      }
      if (applied.customerMobileContains.trim()) {
        params.customerMobileContains = applied.customerMobileContains.trim();
      }
      if (applied.itemTitleContains.trim()) {
        params.itemTitleContains = applied.itemTitleContains.trim();
      }
      const from = startOfDayIso(applied.dateFrom);
      const to = endOfDayIso(applied.dateTo);
      if (from) params.createdFrom = from;
      if (to) params.createdTo = to;

      const raw = await adminApi.listOrderHistory(params);
      const norm = normalizeListResponse(raw, limit);
      setRows(norm.items);
      setTotalCount(norm.totalCount);
    } catch (e) {
      setError(pickErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [skip, limit, applied]);

  useEffect(() => {
    load();
  }, [load]);

  function applyFilters() {
    setApplied({ ...draft });
    setSkip(0);
  }

  function clearFilters() {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setSkip(0);
  }

  function updateDraft(field, value) {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  const columns = [
    {
      key: 'orderNumber',
      label: 'Order',
      width: '12rem',
      render: (r) => {
        const id = orderRowId(r);
        const no = orderDisplayNumber(r);
        const title = formatOrderTitleSummary(r);
        return (
          <div>
            <Link to={id ? `/orders/${id}` : '#'} className="fw-semibold text-decoration-none">
              {no}
            </Link>
            {title ? (
              <div className="small text-secondary order-items-summary mt-1">{title}</div>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'type',
      label: 'Type',
      width: '7.5rem',
      render: (r) => {
        const isPickup = isPickupOrder(r);
        return isPickup ? (
          <span className="badge text-bg-warning text-dark">🏬 Pickup</span>
        ) : (
          <span className="badge text-bg-light border text-secondary">🛵 Delivery</span>
        );
      },
    },
    {
      key: 'placed',
      label: 'Placed',
      width: '11rem',
      render: (r) => {
        const iso = r.placedAt || r.createdAt || r.created_at || r.orderedAt;
        return (
          <div className="small">
            <div className="text-dark text-nowrap">{formatPlacedDate(iso)}</div>
            <div className="text-secondary order-placed-time text-nowrap">{formatPlacedTime(iso)}</div>
          </div>
        );
      },
    },
    {
      key: 'customer',
      label: 'Customer',
      width: '11rem',
      render: (r) => {
        const custName = pickCustomerName(r);
        const recipient = pickDeliveryRecipientName(r);
        const name = custName && custName.toLowerCase() !== 'user' ? custName : (recipient || custName);
        const uid = pickAppUserId(r);
        return (
          <div className="small">
            {name ? (
              <div className="fw-medium text-dark text-truncate" title={name}>
                {name}
              </div>
            ) : (
              <span className="text-secondary">—</span>
            )}
            {uid ? (
              <Link to={`/app-users/${uid}`} className="text-decoration-none" onClick={(e) => e.stopPropagation()}>
                User
              </Link>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'contact',
      label: 'Contact',
      width: '12rem',
      render: (r) => {
        const em = pickCustomerEmail(r);
        const ph = pickDeliveryContactPhone(r) || pickCustomerPhone(r);
        return (
          <div className="small text-secondary">
            {em ? (
              <div className="text-truncate" title={em}>
                {em}
              </div>
            ) : null}
            {ph ? <div className="text-truncate">{ph}</div> : null}
            {!em && !ph ? '—' : null}
          </div>
        );
      },
    },
    {
      key: 'items',
      label: 'Items',
      width: '4rem',
      render: (r) => {
        const n = lineItemCount(r);
        return n != null ? <span className="tabular-nums">{n}</span> : <span className="text-secondary">—</span>;
      },
    },
    {
      key: 'total',
      label: 'Total',
      width: '7rem',
      render: (r) => <span className="tabular-nums fw-medium">{formatMoney(pickTotalAmount(r))}</span>,
    },
    {
      key: 'payment',
      label: 'Pay',
      width: '7rem',
      render: (r) => {
        const { method, status: ps } = pickPaymentSummary(r);
        const bit = [method, ps].filter(Boolean).join(' · ');
        return bit ? (
          <span className="small text-secondary text-truncate d-block" title={bit}>
            {bit}
          </span>
        ) : (
          '—'
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: '9.5rem',
      render: (r) => statusBadge(r.status),
    },
    {
      key: 'actions',
      label: '',
      width: '11.5rem',
      render: (r) => {
        const id = orderRowId(r);
        const orderNo = orderDisplayNumber(r);
        if (!id && (!orderNo || orderNo === '—')) return '—';
        const placedAt = r.placedAt || r.createdAt || r.created_at || r.orderedAt;
        return (
          <div className="table-actions">
            {id ? (
              <Link to={`/orders/${id}`} className="btn btn-sm btn-outline-primary">
                Details
              </Link>
            ) : null}
            <CommissionFlowLink orderNo={orderNo} from="/order-history" placedAt={placedAt} />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader title="Order history" subtitle="Delivered, refunded, and cancelled orders" />
      <ErrorBanner message={error} />
      <Card className="mb-3">
        <CardBody>
          <div className="row g-2 align-items-end">
            <div className="col-6 col-md-4 col-lg-2">
              <label className="form-label small mb-1" htmlFor="history-filter-status">
                Status
              </label>
              <select
                id="history-filter-status"
                className="form-select"
                value={draft.status}
                onChange={(e) => updateDraft('status', e.target.value)}
              >
                <option value="">All Orders</option>
                {HISTORY_ORDER_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {humanOrderStatus(s)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <label className="form-label small mb-1" htmlFor="history-filter-type">
                Type
              </label>
              <select
                id="history-filter-type"
                className="form-select"
                value={draft.orderType}
                onChange={(e) => updateDraft('orderType', e.target.value)}
              >
                <option value="">All Types</option>
                <option value="DELIVERY">🛵 Delivery</option>
                <option value="PICKUP">🏬 Self Pickup</option>
              </select>
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label small mb-1" htmlFor="history-filter-order">
                Order #
              </label>
              <input
                id="history-filter-order"
                className="form-control"
                value={draft.orderNumberContains}
                onChange={(e) => updateDraft('orderNumberContains', e.target.value)}
                placeholder="Search number…"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilters();
                }}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label small mb-1" htmlFor="history-filter-name">
                Customer name
              </label>
              <input
                id="history-filter-name"
                className="form-control"
                value={draft.customerNameContains}
                onChange={(e) => updateDraft('customerNameContains', e.target.value)}
                placeholder="Name contains…"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilters();
                }}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label small mb-1" htmlFor="history-filter-mobile">
                Mobile
              </label>
              <input
                id="history-filter-mobile"
                className="form-control"
                value={draft.customerMobileContains}
                onChange={(e) => updateDraft('customerMobileContains', e.target.value)}
                placeholder="Phone contains…"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilters();
                }}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <label className="form-label small mb-1" htmlFor="history-filter-item">
                Item
              </label>
              <input
                id="history-filter-item"
                className="form-control"
                value={draft.itemTitleContains}
                onChange={(e) => updateDraft('itemTitleContains', e.target.value)}
                placeholder="Dish name…"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilters();
                }}
              />
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <label className="form-label small mb-1" htmlFor="history-filter-from">
                From
              </label>
              <input
                id="history-filter-from"
                className="form-control"
                type="date"
                value={draft.dateFrom}
                onChange={(e) => updateDraft('dateFrom', e.target.value)}
              />
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <label className="form-label small mb-1" htmlFor="history-filter-to">
                To
              </label>
              <input
                id="history-filter-to"
                className="form-control"
                type="date"
                value={draft.dateTo}
                onChange={(e) => updateDraft('dateTo', e.target.value)}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-4 d-flex flex-wrap gap-2">
              <div className="flex-grow-1" style={{ minWidth: '8rem' }}>
                <label className="form-label small mb-1 text-transparent user-select-none" aria-hidden="true">
                  Apply
                </label>
                <FilterSubmitButton onClick={applyFilters} disabled={loading} />
              </div>
              <div className="flex-grow-1" style={{ minWidth: '8rem' }}>
                <label className="form-label small mb-1 text-transparent user-select-none" aria-hidden="true">
                  Clear
                </label>
                <button type="button" className="btn btn-outline-secondary w-100" onClick={clearFilters} disabled={loading}>
                  Clear filters
                </button>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
      <Card className="admin-orders-table-card">
        <CardBody className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status" aria-label="Loading" />
            </div>
          ) : (
            <div className="admin-orders-scroll-boundary">
              <DataTable
                columns={columns}
                rows={rows}
                emptyText="No closed orders match these filters."
                rowKey={(r, i) => orderRowId(r) || i}
              />
            </div>
          )}
          <Pagination totalCount={totalCount} skip={skip} limit={limit} onChange={({ skip: s }) => setSkip(s)} />
        </CardBody>
      </Card>
    </>
  );
}
